import React from 'react';
import { fmt } from '../utils/api';

export default function MagiTracker({ data, onRefresh }) {
  if (!data) return <TrackerSkeleton />;

  const {
    ytdIncome,
    projectedYearEnd,
    limits,
    pctOf400FPL,
    projectedPctOf400,
    remainingUnder400,
    projectedDeltaFrom400,
    status,
    currentYear,
  } = data;

  const barPct = Math.min(pctOf400FPL, 100);
  const statusColor =
    status === 'good' ? '#00d4aa' : status === 'warning' ? '#fbbf24' : '#ef4444';

  return (
    <div style={styles.tracker}>
      <div style={styles.header}>
        <h2 style={styles.title}>🏥 MAGI Healthcare Tracker</h2>
        <span style={styles.year}>{currentYear}</span>
      </div>

      <div style={styles.explainer}>
        <strong style={styles.explainerStrong}>What this is:</strong> if you buy
        health insurance through the ACA Marketplace, your subsidy depends on
        your MAGI (Modified Adjusted Gross Income) staying under 400% of the
        Federal Poverty Line for your household size. Go over that line and
        you can lose your subsidy entirely - sometimes owing thousands back at
        tax time. This tracks your income against that limit all year, so
        there are no surprises in April.
      </div>

      <div style={styles.limitInfo}>
        <span style={styles.limitLabel}>ACA 400% FPL Limit (HH of 2)</span>
        <span style={styles.limitAmt}>{fmt.currency(limits?.fpl400)}</span>
      </div>

      {/* Main progress bar */}
      <div style={styles.barSection}>
        <div style={styles.barLabels}>
          <span style={{ color: statusColor, fontFamily: "'DM Mono', monospace", fontSize: '0.8rem' }}>
            YTD: {fmt.currency(ytdIncome)} ({pctOf400FPL}%)
          </span>
          <span style={{ color: '#6b7280', fontFamily: "'DM Mono', monospace", fontSize: '0.8rem' }}>
            Limit: {fmt.currency(limits?.fpl400)}
          </span>
        </div>
        <div style={styles.barTrack}>
          {/* 138% FPL marker (Medicaid) */}
          <div style={{
            ...styles.marker,
            left: `${Math.min((limits?.fpl138 / limits?.fpl400) * 100, 100)}%`,
          }} />
          <div
            style={{
              ...styles.barFill,
              width: `${barPct}%`,
              background: statusColor,
              boxShadow: `0 0 10px ${statusColor}50`,
            }}
          />
        </div>
        <div style={styles.markerLabels}>
          <span style={styles.markerLabel} />
          <span style={{ ...styles.markerLabel, color: '#6b7280', fontSize: '0.65rem' }}>
            138% FPL = Medicaid
          </span>
          <span style={styles.markerLabel} />
        </div>
      </div>

      {/* Stats grid */}
      <div className="stat-grid-3" style={styles.grid}>
        <StatBox
          label="Remaining Under Limit"
          value={fmt.currency(remainingUnder400)}
          color={remainingUnder400 > 0 ? '#00d4aa' : '#ef4444'}
        />
        <StatBox
          label="Projected Year-End"
          value={fmt.currency(projectedYearEnd)}
          color={statusColor}
          sub={`${projectedPctOf400}% of limit`}
        />
        <StatBox
          label="Projected Delta"
          value={
            projectedDeltaFrom400 >= 0
              ? `${fmt.currency(projectedDeltaFrom400)} under`
              : `${fmt.currency(Math.abs(projectedDeltaFrom400))} OVER`
          }
          color={projectedDeltaFrom400 >= 0 ? '#00d4aa' : '#ef4444'}
        />
      </div>

      {/* Status message */}
      <div style={{
        ...styles.statusMsg,
        background: `${statusColor}10`,
        borderColor: `${statusColor}30`,
        color: statusColor,
      }}>
        {status === 'good' && `✓ You're on track to stay under the ACA limit. ${fmt.currency(projectedDeltaFrom400)} buffer projected.`}
        {status === 'warning' && `⚠ Caution: projected income is within 10% of the ACA limit.`}
        {status === 'over' && `✗ Projected income exceeds the ACA limit by ${fmt.currency(Math.abs(projectedDeltaFrom400))}.`}
      </div>

    </div>
  );
}

function StatBox({ label, value, color, sub }) {
  return (
    <div style={styles.statBox}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color }}>{value}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

function TrackerSkeleton() {
  return (
    <div style={styles.tracker}>
      <div style={{ ...styles.skeleton, height: 24, width: '60%', marginBottom: 16 }} />
      <div style={{ ...styles.skeleton, height: 12, width: '100%', marginBottom: 8 }} />
      <div style={{ ...styles.skeleton, height: 8, width: '100%', marginBottom: 20 }} />
    </div>
  );
}

const styles = {
  tracker: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  year: {
    color: '#6b7280',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  explainer: {
    background: 'rgba(147, 51, 234, 0.08)',
    border: '1px solid rgba(147, 51, 234, 0.25)',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    color: '#c4b5fd',
    fontSize: '0.8rem',
    lineHeight: '1.6',
    marginBottom: '1rem',
  },
  explainerStrong: { color: '#fff' },
  limitInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  limitLabel: { color: '#9ca3af', fontSize: '0.8rem', fontFamily: "'Syne', sans-serif" },
  limitAmt: {
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  barSection: { marginBottom: '1rem' },
  barLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.4rem',
  },
  barTrack: {
    position: 'relative',
    height: '10px',
    background: '#1e2a3a',
    borderRadius: '5px',
    overflow: 'visible',
  },
  barFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.6s ease',
  },
  marker: {
    position: 'absolute',
    top: '-3px',
    height: '16px',
    width: '2px',
    background: '#374151',
    zIndex: 2,
  },
  markerLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '4px',
  },
  markerLabel: { flex: 1, textAlign: 'center' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  statBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.75rem',
  },
  statLabel: {
    color: '#6b7280',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  statValue: {
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  statSub: {
    color: '#4b5563',
    fontSize: '0.65rem',
    marginTop: '2px',
    fontFamily: "'DM Mono', monospace",
  },
  statusMsg: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '0.8rem',
    fontWeight: '500',
    marginBottom: '1.25rem',
    fontFamily: "'Syne', sans-serif",
  },
  incomeSection: {},
  incomeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  incomeTitle: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "'Syne', sans-serif",
  },
  addBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#00d4aa',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
  },
  incomeForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    padding: '0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    border: '1px solid #1e2a3a',
  },
  input: {
    padding: '0.5rem 0.75rem',
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    outline: 'none',
  },
  saveBtn: {
    padding: '0.5rem',
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  error: {
    color: '#ef4444',
    fontSize: '0.8rem',
    padding: '0.4rem 0.6rem',
    background: 'rgba(239,68,68,0.1)',
    borderRadius: '4px',
  },
  incomeList: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  incomeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '6px',
    border: '1px solid #1e2a3a',
  },
  incomeSource: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    marginRight: '0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
  incomeDate: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  incomeAmt: {
    color: '#00d4aa',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.7rem',
    padding: '2px 4px',
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.8rem',
    textAlign: 'center',
    padding: '0.75rem 0',
    margin: 0,
  },
  skeleton: {
    background: 'linear-gradient(90deg, #1e2a3a 25%, #263347 50%, #1e2a3a 75%)',
    borderRadius: '4px',
  },
};
